// Formatação numérica compartilhada entre os renderizadores da proposta de Manejo —
// card React (client/src/components/manejo/proposta-card.tsx) e, quando existir, o
// texto de WhatsApp do Agente (Rizoma_Agente_Whatsapp_Arquitetura.md §3). A proposta
// em si é dado puro (nunca frase pronta) exatamente pra permitir dois renderizadores
// — mas um NÚMERO DERIVADO (calculado a partir de outros números, não só formatado)
// calculado dentro de um componente escapa dessa regra: cada renderer recalcularia
// por conta própria e podem divergir ("5,5×" no card × "5,4×" na mensagem pra
// exatamente a mesma correção). Esta é a ÚNICA fonte da forma como esses números
// aparecem — nenhum componente calcula ou formata por conta própria.

const FORMATADOR_KG = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const FORMATADOR_FATOR = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** kg (ou kg/ha) em pt-BR, até 2 casas decimais — mesma forma em qualquer renderer. */
export function formatarKg(valor: number): string {
  return FORMATADOR_KG.format(valor);
}

/**
 * Fator de desvio de uma razão contra a mediana da coorte (ex.: "5,5×"), usado na
 * correção `razao_produtos` (ver `validarProposta` em server/lib/manejo/interpretar.ts,
 * mesma conta: `razaoAtual / mediana`). `null` quando a mediana é <= 0 (divisão
 * indefinida) — o chamador decide o texto de fallback, esta função nunca inventa "?".
 */
export function formatarFatorDesvio(razaoAtual: number, mediana: number): string | null {
  if (mediana <= 0) return null;
  return `${FORMATADOR_FATOR.format(razaoAtual / mediana)}×`;
}

// ─── Entrada da criação manual (origem 'manual') ──────────────────────────────
// `mnj_lote.entrada_raw` é o recibo de auditoria da entrada: no texto livre é o que
// o usuário colou, no áudio é a transcrição. Na criação manual não existe texto de
// origem — este resumo ocupa esse lugar, pra que o lote continue auditável ("de onde
// veio esta operação?") em vez de guardar uma string vazia ou genérica. Data em ISO
// de propósito: é registro, não tela.

export interface EntradaManualResumo {
  atividadeNome: string;
  /** Texto livre do usuário — quando existe, é ELE o assunto da operação. */
  titulo?: string | null;
  /** `null` = foi pro backlog. Registrar uma data que a operação não tem seria mentira no recibo. */
  dataPrevista: string | null; // yyyy-mm-dd
  setoresNomes: string[];
  percentualAplicado: number;
  /** Instrução para quem executa — no registro manual é onde os insumos costumam estar, escritos à mão. */
  observacao?: string | null;
  /** Vazio é o caso COMUM: insumo estruturado é opcional no formulário. */
  itens: Array<{ produtoNome: string; kgTotal: number }>;
}

export function descreverEntradaManual(resumo: EntradaManualResumo): string {
  const titulo = resumo.titulo?.trim();
  // Título e atividade aparecem os dois: o recibo tem que dizer o que a pessoa
  // escreveu E sob qual atividade isso foi arquivado.
  const cabeca = titulo ? `${titulo} (${resumo.atividadeNome})` : resumo.atividadeNome;
  const partes = [cabeca];
  if (resumo.setoresNomes.length > 0) {
    const percentual = resumo.percentualAplicado === 100 ? "" : ` (${formatarKg(resumo.percentualAplicado)}% aplicado)`;
    partes.push(`${resumo.setoresNomes.join(", ")}${percentual}`);
  }
  partes.push(resumo.dataPrevista ?? "sem data (backlog)");

  let texto = `[manual] ${partes.join(" — ")}`;
  if (resumo.itens.length > 0) {
    texto += `: ${resumo.itens.map(it => `${it.produtoNome} ${formatarKg(it.kgTotal)}kg`).join(", ")}`;
  }
  const observacao = resumo.observacao?.trim();
  if (observacao) texto += ` — "${observacao}"`;
  return texto;
}
